import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { BookOpen, Layers } from 'lucide-react';
import { passageAPI, progressAPI, leaderboardAPI } from '../services/api';
import FlashcardSets from './FlashcardSets';
import MiniLeaderboard from './MiniLeaderboard';
import TrophyShelf from './TrophyShelf';
import CarrotCatchGame from './CarrotCatchGame';
import api from '../services/api';

const StudySessionSelector = ({ profile, onSelectSession, onBack }) => {
  const [sessions, setSessions] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBrowse, setShowBrowse] = useState(false);
  const [showFlashcards, setShowFlashcards] = useState(false);
  const [flashcardCount, setFlashcardCount] = useState(0);
  const [sessionPoints, setSessionPoints] = useState({}); // Store points per session
  const [redeemableStatus, setRedeemableStatus] = useState({}); // Track which sessions can be redeemed
  const [showRedeemDialog, setShowRedeemDialog] = useState(false);
  const [selectedSessionForRedeem, setSelectedSessionForRedeem] = useState(null);
  const [showMiniGame, setShowMiniGame] = useState(false);
  const [questionnaire, setQuestionnaire] = useState({
    quiz_out: false,
    quiz_out_count: 1,
    round_win: false,
    round_win_count: 1,
    bowl_compete: false,
    bowl_win: false
  });
  const [isRedeeming, setIsRedeeming] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchSessions();
    loadFlashcardCount();
  }, []);

  const loadFlashcardCount = async () => {
    try {
      const response = await api.get('/flashcards/my-sets');
      setFlashcardCount(response.data.length);
    } catch (error) {
      console.error('Error loading flashcard count:', error);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/study-sessions`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      // Get progress for each session
      const sessionsWithProgress = await Promise.all(
        data.sessions.map(async (session) => {
          try {
            const sessionIdTrimmed = (session.session_id || '').trim();
            const progressResponse = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/study-sessions/${sessionIdTrimmed}/progress-summary?profile_id=${profile.id}`,
              { credentials: 'include' }
            );
            const progressData = await progressResponse.json();
            
            // Fetch session points for this profile
            try {
              const pointsData = await leaderboardAPI.getProfileSessionPoints(sessionIdTrimmed, profile.id);
              setSessionPoints(prev => ({
                ...prev,
                [sessionIdTrimmed]: pointsData.points || 0
              }));
            } catch (pointsError) {
              console.error('Error fetching session points:', pointsError);
            }

            // Trophy redemption status is now calculated locally from progress data
            // No need for separate API call
            
            return {
              ...session,
              progress: progressData
            };
          } catch (error) {
            return {
              ...session,
              progress: null
            };
          }
        })
      );
      
      setAllSessions(sessionsWithProgress);
      
      // Filter to only show sessions with progress (started sessions) and not yet redeemed
      const startedSessions = sessionsWithProgress.filter(
        session => session.progress && session.progress.completed_days && session.progress.completed_days.length > 0 && !session.progress.trophy_redeemed
      );
      
      setSessions(startedSessions);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRedeemClick = (session, e) => {
    e.stopPropagation(); // Prevent session card click
    setSelectedSessionForRedeem(session);
    setQuestionnaire({
      quiz_out: false,
      quiz_out_count: 1,
      round_win: false,
      round_win_count: 1,
      bowl_compete: false,
      bowl_win: false
    });
    setShowRedeemDialog(true);
  };

  const handleRedeemSubmit = async () => {
    console.log('[handleRedeemSubmit] Called');
    console.log('[handleRedeemSubmit] selectedSessionForRedeem:', selectedSessionForRedeem);
    console.log('[handleRedeemSubmit] profile.id:', profile.id);
    console.log('[handleRedeemSubmit] questionnaire:', questionnaire);
    
    if (!selectedSessionForRedeem) {
      console.log('[handleRedeemSubmit] No session selected, returning');
      return;
    }

    try {
      setIsRedeeming(true);
      console.log('[handleRedeemSubmit] Sending API request...');
      
      const response = await api.post('/trophies/redeem', {
        session_id: selectedSessionForRedeem.session_id,
        profile_id: profile.id,
        questionnaire: questionnaire
      });
      
      console.log('[handleRedeemSubmit] API response:', response);

      if (response.data.success) {
        console.log('Trophy redemption response:', response.data);
        
        // Build detailed success message
        const trophies = response.data.trophies;
        let message = `🏆 Congratulations! You've earned ${trophies.length} trophy/trophies!\n\n`;
        
        // Group trophies by type for display
        const groupedByType = {};
        trophies.forEach(trophy => {
          const type = trophy.trophy_type;
          groupedByType[type] = (groupedByType[type] || 0) + 1;
        });
        
        Object.entries(groupedByType).forEach(([type, count]) => {
          message += `${count}x ${type.replace('_', ' ').toUpperCase()}\n`;
        });
        
        alert(message);
        
        // Close dialog
        setShowRedeemDialog(false);
        setSelectedSessionForRedeem(null);
        
        // Hard refresh the page to show updated trophies immediately
        window.location.reload();
      }
    } catch (error) {
      console.error('Error redeeming trophy:', error);
      alert('Failed to redeem trophy. Please try again.');
    } finally {
      setIsRedeeming(false);
    }
  };

  const handleDeleteSession = async (session, e) => {
    e.stopPropagation(); // Prevent session card click
    
    if (!window.confirm(`Are you sure you want to delete "${session.title}"? This will remove all your progress for this session.`)) {
      return;
    }

    try {
      // Delete progress for this session
      await api.delete(`/progress/session/${profile.id}/${session.session_id}`);
      
      // Show success message
      alert('Session deleted successfully');
      
      // Refresh sessions
      fetchSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      alert('Failed to delete session. Please try again.');
    }
  };

  const getProgressPercentage = (session) => {
    if (!session.progress) return 0;
    const total = session.progress.total_lessons || 1;
    const completed = session.progress.completed_days?.length || 0;
    return Math.round((completed / total) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage === 0) return 'bg-gray-200';
    if (percentage < 50) return 'bg-yellow-400';
    if (percentage < 100) return 'bg-blue-400';
    return 'bg-green-500';
  };

  if (showFlashcards) {
    return <FlashcardSets onBack={() => setShowFlashcards(false)} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      {/* Sticky Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-br from-blue-50 to-indigo-100 pb-4 pt-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Welcome back, {profile.name}! 📖
          </h1>
          <p className="text-gray-600">
            {showBrowse ? 'Browse and add new study sessions' : 'Your active study sessions'}
          </p>
        </div>
      </div>

      {/* Trophy Shelf - Only show in active sessions view */}
      {!showBrowse && !showFlashcards && (
        <div className="w-full max-w-6xl mx-auto mb-6">
          <TrophyShelf 
            userId={profile.user_id} 
            profileId={profile.id}
          />
        </div>
      )}

      {/* Scrollable Content */}
      <div className="w-full max-w-4xl mx-auto">

        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">Loading study sessions...</p>
            </CardContent>
          </Card>
        ) : showBrowse ? (
          // Browse all available sessions
          <div className="space-y-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-900">All Available Sessions</h2>
              <Button onClick={() => setShowBrowse(false)} variant="outline">
                ← Back to My Sessions
              </Button>
            </div>
            
            {allSessions.map((session) => {
              const progressPercentage = getProgressPercentage(session);
              const completedDays = session.progress?.completed_days?.length || 0;
              const totalLessons = session.progress?.total_lessons || 0;
              const currentDay = session.progress?.current_day || 1;
              const hasStarted = completedDays > 0;
              
              return (
                <Card 
                  key={session.session_id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => onSelectSession(session)}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">
                          {session.title}
                          {hasStarted && <span className="ml-2 text-sm text-blue-600">(In Progress)</span>}
                        </CardTitle>
                        <CardDescription>{session.description}</CardDescription>
                        <p className="text-sm text-gray-500 mt-2">
                          <strong>Reference:</strong> {session.passage_reference}
                        </p>
                      </div>
                      {hasStarted && (
                        <div className="ml-4 text-right">
                          <div className="text-2xl font-bold text-blue-600">
                            {progressPercentage}%
                          </div>
                          <div className="text-xs text-gray-500">Complete</div>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {hasStarted && (
                        <>
                          {/* Progress Bar */}
                          <div className="w-full bg-gray-200 rounded-full h-2.5">
                            <div
                              className={`h-2.5 rounded-full ${getProgressColor(progressPercentage)}`}
                              style={{ width: `${progressPercentage}%` }}
                            ></div>
                          </div>
                          
                          {/* Stats */}
                          <div className="flex justify-between items-center text-sm">
                            <div className="flex gap-4">
                              <span className="text-gray-600">
                                <strong>Completed:</strong> {completedDays}/{totalLessons}
                              </span>
                              <span className="text-green-600 font-semibold">
                                <strong>⭐ Points:</strong> {sessionPoints[session.session_id?.trim()] || 0}
                              </span>
                              {progressPercentage > 0 && progressPercentage < 100 && (
                                <span className="text-blue-600 font-medium">
                                  Day {currentDay} is next
                                </span>
                              )}
                            </div>
                            <Button variant="default" size="sm">
                              Continue →
                            </Button>
                          </div>
                          
                          {/* Session Leaderboard */}
                          <div className="mt-3">
                            <MiniLeaderboard 
                              type="session"
                              sessionId={session.session_id}
                              profileId={profile.id}
                              title="Session Leaders (Top 5)"
                            />
                          </div>
                        </>
                      )}
                      {!hasStarted && (
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">
                            <strong>Lessons:</strong> {totalLessons}
                          </span>
                          <Button variant="default" size="sm">
                            Start Session →
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : sessions.length === 0 ? (
          // No active sessions - show browse option
          <Card>
            <CardContent className="p-8 text-center">
              <div className="mb-4">
                <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Sessions</h3>
                <p className="text-gray-600 mb-6">
                  Start your journey by browsing available study sessions
                </p>
              </div>
              <Button onClick={() => setShowBrowse(true)} size="lg" className="mb-4">
                📚 Browse Study Sessions
              </Button>
              <div>
                <Button onClick={onBack} variant="outline">
                  ← Back to Profiles
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : (
          // Show active sessions with add button
          <div className="space-y-4">
            {sessions.map((session) => {
              const progressPercentage = getProgressPercentage(session);
              const completedDays = session.progress?.completed_days?.length || 0;
              const totalLessons = session.progress?.total_lessons || 0;
              const currentDay = session.progress?.current_day || 1;
              
              return (
                <Card 
                  key={session.session_id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => onSelectSession(session)}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-xl mb-2">{session.title}</CardTitle>
                        <CardDescription>{session.description}</CardDescription>
                        <p className="text-sm text-gray-500 mt-2">
                          <strong>Reference:</strong> {session.passage_reference}
                        </p>
                      </div>
                      {progressPercentage > 0 && (
                        <div className="ml-4 text-right">
                          <div className="text-2xl font-bold text-blue-600">
                            {progressPercentage}%
                          </div>
                          <div className="text-xs text-gray-500">Complete</div>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {/* Progress Bar */}
                      <div className="w-full bg-gray-200 rounded-full h-2.5">
                        <div
                          className={`h-2.5 rounded-full ${getProgressColor(progressPercentage)}`}
                          style={{ width: `${progressPercentage}%` }}
                        ></div>
                      </div>
                      
                      {/* Stats */}
                      <div className="flex justify-between items-center text-sm">
                        <div className="flex gap-4">
                          <span className="text-gray-600">
                            <strong>Lessons:</strong> {totalLessons}
                          </span>
                          <span className="text-gray-600">
                            <strong>Completed:</strong> {completedDays}/{totalLessons}
                          </span>
                          <span className="text-green-600 font-semibold">
                            <strong>⭐ Points:</strong> {sessionPoints[session.session_id?.trim()] || 0}
                          </span>
                          {progressPercentage > 0 && progressPercentage < 100 && (
                            <span className="text-blue-600 font-medium">
                              Day {currentDay} is next
                            </span>
                          )}
                        </div>
                        <Button variant="default" size="sm">
                          {progressPercentage === 0 ? 'Start' : progressPercentage === 100 ? 'Review' : 'Continue'} →
                        </Button>
                      </div>

                      {/* Trophy Redemption Section - ALWAYS VISIBLE */}
                      {(() => {
                        // Calculate progress toward 450 points per lesson
                        const dayProgressRaw = session.progress?.day_progress;
                        const totalLessons = session.progress?.total_lessons || 0;
                        
                        // Handle both array and object formats for day_progress
                        let lessonsAt450 = 0;
                        if (Array.isArray(dayProgressRaw)) {
                          // Array format: [{lesson_id, earned_points}, ...]
                          lessonsAt450 = dayProgressRaw.filter(day => day?.earned_points >= 450).length;
                        } else if (dayProgressRaw && typeof dayProgressRaw === 'object') {
                          // Object format: {'1': {earned_points: 100}, '2': {earned_points: 50}}
                          lessonsAt450 = Object.values(dayProgressRaw).filter(day => day?.earned_points >= 450).length;
                        }
                        
                        const canRedeem = lessonsAt450 === totalLessons && totalLessons > 0;
                        const alreadyRedeemed = session.progress?.trophy_redeemed || false;
                        const progressPercent = totalLessons > 0 ? Math.round((lessonsAt450 / totalLessons) * 100) : 0;

                        return (
                          <div className="mt-4 space-y-2">
                            {/* Trophy Progress Bar */}
                            <div className="p-3 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-lg">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-sm font-semibold text-amber-900">
                                  🏆 Trophy Progress: {lessonsAt450}/{totalLessons} lessons at 450+ points
                                </span>
                                {alreadyRedeemed && (
                                  <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-semibold">
                                    ✓ Redeemed
                                  </span>
                                )}
                              </div>
                              
                              {/* Progress Bar */}
                              <div className="w-full bg-amber-200 rounded-full h-3">
                                <div
                                  className={`h-3 rounded-full transition-all ${
                                    progressPercent === 100 ? 'bg-green-500' : 'bg-amber-500'
                                  }`}
                                  style={{ width: `${progressPercent}%` }}
                                ></div>
                              </div>
                              
                              {!canRedeem && !alreadyRedeemed && (
                                <p className="text-xs text-amber-700 mt-2">
                                  Complete all lessons with 450+ points to unlock redemption
                                </p>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-2">
                              <Button 
                                onClick={(e) => handleRedeemClick(session, e)}
                                disabled={!canRedeem || alreadyRedeemed}
                                className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:opacity-50"
                                size="sm"
                              >
                                {alreadyRedeemed ? '✓ Trophy Redeemed' : 'Redeem My Trophy'}
                              </Button>
                              
                              <Button 
                                onClick={(e) => handleDeleteSession(session, e)}
                                variant="outline"
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                size="sm"
                              >
                                Delete Session
                              </Button>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Session Leaderboard */}
                      <div className="mt-4">
                        <MiniLeaderboard 
                          type="session"
                          sessionId={session.session_id}
                          profileId={profile.id}
                          title="Session Leaders (Top 5)"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Flashcard Sets Button */}
            {flashcardCount > 0 && (
              <Card className="border-2 border-purple-300 bg-gradient-to-br from-purple-50 to-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Layers className="w-8 h-8 text-purple-600" />
                      <div>
                        <h3 className="text-lg font-semibold text-purple-900">My Flashcard Sets</h3>
                        <p className="text-sm text-purple-700">
                          {flashcardCount} flashcard {flashcardCount === 1 ? 'set' : 'sets'} available
                        </p>
                      </div>
                    </div>
                    <Button 
                      onClick={() => setShowFlashcards(true)}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      View Flashcards →
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Add More Sessions Button */}
            <Card className="border-2 border-dashed border-blue-300 hover:border-blue-500 transition-colors">
              <CardContent className="p-8 text-center">
                <Button 
                  onClick={() => setShowBrowse(true)} 
                  variant="outline" 
                  size="lg"
                  className="w-full"
                >
                  ➕ Browse More Study Sessions
                </Button>
              </CardContent>
            </Card>

            <div className="text-center mt-6">
              <Button onClick={onBack} variant="outline">
                ← Back to Profiles
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Trophy Redemption Dialog */}
      {showRedeemDialog && selectedSessionForRedeem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              🏆 Redeem Your Trophy!
            </h2>
            <p className="text-gray-600 mb-6">
              Congratulations on completing <strong>{selectedSessionForRedeem.title}</strong>! 
              Answer a few questions to determine which trophies you've earned.
            </p>

            <div className="space-y-4 mb-6">
              {/* FIRST QUESTION: Bowl Compete (Y/N) */}
              <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                <label className="text-base font-bold text-gray-900 block mb-3">
                  Did you compete in a quiz bowl?
                </label>
                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setQuestionnaire({ 
                      ...questionnaire, 
                      bowl_compete: true,
                      bowl_win: false
                    })}
                    className={`flex-1 py-2 px-4 rounded font-semibold transition-all ${
                      questionnaire.bowl_compete 
                        ? 'bg-green-600 text-white shadow-lg' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    YES
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuestionnaire({ 
                      ...questionnaire, 
                      bowl_compete: false,
                      bowl_win: false,
                      quiz_out: false,
                      quiz_out_count: 1,
                      round_win: false,
                      round_win_count: 1
                    })}
                    className={`flex-1 py-2 px-4 rounded font-semibold transition-all ${
                      !questionnaire.bowl_compete 
                        ? 'bg-red-600 text-white shadow-lg' 
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    NO
                  </button>
                </div>
              </div>

              {/* Show mini-game message if NO to bowl compete */}
              {!questionnaire.bowl_compete && (
                <div className="p-4 bg-purple-50 border-2 border-purple-300 rounded-lg text-center">
                  <p className="text-purple-900 font-semibold mb-2">🎮 Ready for a Mini-Game?</p>
                  <p className="text-sm text-purple-700">
                    Since you didn't compete in a quiz bowl, you'll get a chance to earn a bonus trophy through a fun mini-game!
                  </p>
                </div>
              )}

              {/* CASCADING QUESTIONS - Only show if YES to bowl compete */}
              {questionnaire.bowl_compete && (
                <div className="space-y-4 pl-4 border-l-4 border-blue-300">
                  {/* Quiz Out Question */}
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="quiz_out"
                        checked={questionnaire.quiz_out}
                        onChange={(e) => {
                          setQuestionnaire({ 
                            ...questionnaire, 
                            quiz_out: e.target.checked,
                            quiz_out_count: e.target.checked ? questionnaire.quiz_out_count : 1
                          });
                        }}
                        className="mt-1 mr-3 h-4 w-4"
                      />
                      <label htmlFor="quiz_out" className="text-sm cursor-pointer flex-1">
                        <strong>Did you quiz out?</strong>
                        <p className="text-gray-500 text-xs">Successfully answered all questions correctly without errors</p>
                      </label>
                    </div>
                    
                    {/* Cascading: How many times? */}
                    {questionnaire.quiz_out && (
                      <div className="ml-7 pl-4 border-l-2 border-amber-300">
                        <label className="text-sm font-semibold text-gray-700 block mb-1">
                          How many times?
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={questionnaire.quiz_out_count}
                          onChange={(e) => setQuestionnaire({ 
                            ...questionnaire, 
                            quiz_out_count: parseInt(e.target.value) || 1 
                          })}
                          className="w-20 px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="ml-2 text-xs text-gray-500">trophy/trophies</span>
                      </div>
                    )}
                  </div>

                  {/* Round Win Question */}
                  <div className="space-y-2">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="round_win"
                        checked={questionnaire.round_win}
                        onChange={(e) => {
                          setQuestionnaire({ 
                            ...questionnaire, 
                            round_win: e.target.checked,
                            round_win_count: e.target.checked ? questionnaire.round_win_count : 1
                          });
                        }}
                        className="mt-1 mr-3 h-4 w-4"
                      />
                      <label htmlFor="round_win" className="text-sm cursor-pointer flex-1">
                        <strong>Did you win a quiz round?</strong>
                        <p className="text-gray-500 text-xs">Won a competition round against others</p>
                      </label>
                    </div>
                    
                    {/* Cascading: How many times? */}
                    {questionnaire.round_win && (
                      <div className="ml-7 pl-4 border-l-2 border-amber-300">
                        <label className="text-sm font-semibold text-gray-700 block mb-1">
                          How many times?
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="99"
                          value={questionnaire.round_win_count}
                          onChange={(e) => setQuestionnaire({ 
                            ...questionnaire, 
                            round_win_count: parseInt(e.target.value) || 1 
                          })}
                          className="w-20 px-3 py-1 border border-gray-300 rounded text-sm"
                        />
                        <span className="ml-2 text-xs text-gray-500">trophy/trophies</span>
                      </div>
                    )}
                  </div>

                  {/* Bowl Win Question */}
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="bowl_win"
                      checked={questionnaire.bowl_win}
                      onChange={(e) => setQuestionnaire({ ...questionnaire, bowl_win: e.target.checked })}
                      className="mt-1 mr-3 h-4 w-4"
                    />
                    <label htmlFor="bowl_win" className="text-sm cursor-pointer">
                      <strong>Did you win the quiz bowl?</strong>
                      <p className="text-gray-500 text-xs">Won the quiz bowl competition</p>
                    </label>
                  </div>

                  {/* Show mini-game message if NO quiz out AND NO round win */}
                  {!questionnaire.quiz_out && !questionnaire.round_win && (
                    <div className="p-3 bg-purple-50 border-2 border-purple-300 rounded-lg text-center">
                      <p className="text-sm text-purple-900 font-semibold mb-1">🎮 Mini-Game Opportunity!</p>
                      <p className="text-xs text-purple-700">
                        You participated but didn't quiz out or win a round. Play our mini-game for a bonus trophy!
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowRedeemDialog(false);
                  setSelectedSessionForRedeem(null);
                }}
                variant="outline"
                className="flex-1"
                disabled={isRedeeming}
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  console.log('[Continue Button] Clicked!');
                  console.log('[Continue Button] Questionnaire:', questionnaire);
                  console.log('[Continue Button] isRedeeming:', isRedeeming);
                  
                  // Check if mini-game should be triggered
                  const shouldShowMiniGame = !questionnaire.bowl_compete || 
                    (questionnaire.bowl_compete && !questionnaire.quiz_out && !questionnaire.round_win);
                  
                  console.log('[Continue Button] shouldShowMiniGame:', shouldShowMiniGame);
                  
                  if (shouldShowMiniGame) {
                    // Close dialog and launch mini-game
                    setShowRedeemDialog(false);
                    setShowMiniGame(true);
                  } else {
                    // Proceed directly with redemption
                    console.log('[Continue Button] Calling handleRedeemSubmit...');
                    handleRedeemSubmit();
                  }
                }}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                disabled={isRedeeming}
              >
                {isRedeeming ? 'Redeeming...' : 'Continue'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mini-Game Modal */}
      {showMiniGame && selectedSessionForRedeem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-auto">
            <CarrotCatchGame
              sessionId={selectedSessionForRedeem.session_id}
              profileId={profile.id}
              onComplete={(success = true) => {
                setShowMiniGame(false);
                // Only complete redemption if game was successful or skipped
                // If there was a config error, don't complete redemption
                if (success) {
                  handleRedeemSubmit();
                } else {
                  // Reset questionnaire dialog state so user can try again
                  setSelectedSessionForRedeem(null);
                  setQuestionnaire({
                    quiz_out: false,
                    quiz_out_count: 1,
                    round_win: false,
                    round_win_count: 1,
                    bowl_compete: false,
                    bowl_win: false
                  });
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default StudySessionSelector;
